import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import {
  feedConsumptionApi, feedItemsApi, inventoryItemsApi, inventoryLocationsApi, inventoryLotsApi,
  inventoryTransactionsApi,
} from '../../services/resources';
import { formatCurrency, formatDate, formatNumber, shortId, today } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import type {
  FeedConsumption, FeedItem, InventoryItem, InventoryLocation, InventoryLot, InventoryTransaction,
} from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: the full stock ledger (REQ-021/WF-07) — generic Items,
// storage Locations, lot/expiry tracking and the append-only Transactions
// ledger — plus the Feed-specific masters/consumption tables (REQ-019) that
// feed this same ledger, all live as tabs on one "Inventory" page.

const itemsConfig: ModuleConfig<InventoryItem> = {
  title: 'Items', entityLabel: 'inventory item', module: 'Inventory', queryKey: 'inventory-items', api: inventoryItemsApi, idKey: 'id',
  referenceKeys: ['farms'], defaultValues: { item_type: 'Feed', active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'type', header: 'Type', render: (r) => r.item_type },
    { key: 'unit', header: 'Unit', render: (r) => r.unit },
    { key: 'reorder', header: 'Reorder Level', align: 'right', render: (r) => formatNumber(r.reorder_level, 2) },
    { key: 'active', header: 'Active', render: (r) => (r.active ? 'Yes' : 'No') },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Item', optional: true, optionsKey: 'farms' },
    { name: 'item_type', label: 'Item Type', type: 'select', section: 'Item', required: true, staticOptions: [
      'Feed', 'Medicine', 'Vaccine', 'Semen', 'Equipment', 'SparePart', 'Other',
    ].map((v) => ({ value: v, label: v })) },
    { name: 'name', label: 'Name', type: 'text', section: 'Item', required: true },
    { name: 'unit', label: 'Unit', type: 'text', section: 'Item', required: true },
    { name: 'reorder_level', label: 'Reorder Level', type: 'decimal', section: 'Item', optional: true, min: 0 },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Item' },
  ],
};

const locationsConfig: ModuleConfig<InventoryLocation> = {
  title: 'Locations', entityLabel: 'stock location', module: 'Inventory', queryKey: 'inventory-locations', api: inventoryLocationsApi, idKey: 'id',
  referenceKeys: ['farms', 'locations'], defaultValues: { active: true, location_type: 'Store' },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'type', header: 'Type', render: (r) => r.location_type || '—' },
    { key: 'active', header: 'Active', render: (r) => (r.active ? 'Yes' : 'No') },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Location', required: true, optionsKey: 'farms' },
    { name: 'name', label: 'Name', type: 'text', section: 'Location', required: true },
    { name: 'location_type', label: 'Type', type: 'select', section: 'Location', optional: true, staticOptions: [
      { value: 'Store', label: 'Store' }, { value: 'ColdStorage', label: 'Cold Storage' }, { value: 'Warehouse', label: 'Warehouse' },
    ] },
    { name: 'location_id', label: 'Physical Farm Location', type: 'select', section: 'Location', optional: true, optionsKey: 'locations' },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Location' },
  ],
};

const lotsConfig: ModuleConfig<InventoryLot> = {
  title: 'Lots', entityLabel: 'lot', module: 'Inventory', queryKey: 'inventory-lots', api: inventoryLotsApi, idKey: 'id',
  referenceKeys: ['inventoryItems', 'suppliers', 'inventoryLocations'],
  columns: [
    { key: 'lot', header: 'Lot No.', primary: true, render: (r) => r.lot_no || shortId(r.id) },
    { key: 'item', header: 'Item', render: (r) => shortId(r.item_id) },
    { key: 'available', header: 'Available', align: 'right', render: (r) => formatNumber(r.available_qty, 2) },
    { key: 'received', header: 'Received', align: 'right', render: (r) => formatNumber(r.received_qty, 2), hideOnTablet: true },
    { key: 'cost', header: 'Unit Cost', align: 'right', render: (r) => formatCurrency(r.unit_cost) },
    { key: 'expiry', header: 'Expiry', render: (r) => formatDate(r.expiry_date) },
  ],
  fields: [
    { name: 'item_id', label: 'Item', type: 'select', section: 'Lot', required: true, optionsKey: 'inventoryItems' },
    { name: 'supplier_id', label: 'Supplier', type: 'select', section: 'Lot', optional: true, optionsKey: 'suppliers' },
    { name: 'lot_no', label: 'Lot Number', type: 'text', section: 'Lot', optional: true },
    { name: 'location_id', label: 'Stock Location', type: 'select', section: 'Lot', required: true, optionsKey: 'inventoryLocations' },
    { name: 'manufacture_date', label: 'Manufacture Date', type: 'date', section: 'Dates', optional: true },
    { name: 'expiry_date', label: 'Expiry Date', type: 'date', section: 'Dates', optional: true },
    { name: 'received_qty', label: 'Received Quantity', type: 'decimal', section: 'Quantity', required: true, min: 0 },
    { name: 'unit_cost', label: 'Unit Cost (₹)', type: 'decimal', section: 'Quantity', required: true, min: 0 },
  ],
};

const transactionsConfig: ModuleConfig<InventoryTransaction> = {
  title: 'Transactions', entityLabel: 'transaction', module: 'Inventory', queryKey: 'inventory-transactions',
  api: { list: inventoryTransactionsApi.list, create: async () => { throw new Error('read-only — issue stock from a Lot'); }, update: async () => { throw new Error('read-only'); } },
  idKey: 'id', readOnly: true,
  columns: [
    { key: 'item', header: 'Item', primary: true, render: (r) => shortId(r.item_id) },
    { key: 'type', header: 'Type', render: (r) => <ILMSStatusBadge status={r.transaction_type} tone="neutral" size="sm" /> },
    { key: 'qty', header: 'Quantity', align: 'right', render: (r) => formatNumber(r.quantity, 2) },
    { key: 'at', header: 'When', render: (r) => formatDate(r.transaction_at) },
    { key: 'reason', header: 'Reason', render: (r) => r.reason || '—', hideOnTablet: true },
  ],
  fields: [],
};

const feedItemsConfig: ModuleConfig<FeedItem> = {
  title: 'Feed Items', entityLabel: 'feed item', module: 'Inventory', queryKey: 'feed-items', api: feedItemsApi, idKey: 'id',
  defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'category', header: 'Category', render: (r) => r.category || '—' },
    { key: 'unit', header: 'Unit', render: (r) => r.unit },
    { key: 'reorder', header: 'Reorder Level', align: 'right', render: (r) => formatNumber(r.reorder_level, 2) },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'category', label: 'Category', type: 'select', section: 'Details', optional: true, staticOptions: [
      'Concentrate', 'Roughage', 'Supplement', 'Mineral',
    ].map((v) => ({ value: v, label: v })) },
    { name: 'unit', label: 'Unit', type: 'text', section: 'Details', required: true },
    { name: 'reorder_level', label: 'Reorder Level', type: 'decimal', section: 'Details', optional: true, min: 0 },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

const feedConsumptionConfig: ModuleConfig<FeedConsumption> = {
  title: 'Feed Consumption', entityLabel: 'feed entry', module: 'Inventory', queryKey: 'feed-consumption', api: feedConsumptionApi, idKey: 'id',
  referenceKeys: ['feedItems', 'locations'], addLabel: 'Feed Entry', defaultValues: { consumption_date: today(), unit: 'kg' },
  columns: [
    { key: 'date', header: 'Date', primary: true, render: (r) => formatDate(r.consumption_date) },
    { key: 'item', header: 'Feed Item', render: (r) => shortId(r.feed_item_id) },
    { key: 'group', header: 'Animal Group', render: (r) => r.animal_group || '—', hideOnTablet: true },
    { key: 'quantity', header: 'Quantity', align: 'right', render: (r) => `${formatNumber(r.quantity, 2)} ${r.unit}` },
  ],
  fields: [
    { name: 'consumption_date', label: 'Date', type: 'date', section: 'Feed Entry', required: true },
    { name: 'feed_item_id', label: 'Feed Item', type: 'select', section: 'Feed Entry', required: true, optionsKey: 'feedItems' },
    { name: 'location_id', label: 'Location', type: 'select', section: 'Feed Entry', required: true, optionsKey: 'locations' },
    { name: 'animal_group', label: 'Animal Group', type: 'text', section: 'Feed Entry', optional: true, placeholder: 'e.g. Milking herd' },
    { name: 'quantity', label: 'Quantity', type: 'decimal', section: 'Feed Entry', required: true, min: 0 },
    { name: 'unit', label: 'Unit', type: 'text', section: 'Feed Entry', required: true, placeholder: 'kg' },
  ],
};

export default function InventoryPage() {
  return (
    <TabbedPage
      title="Inventory"
      subtitle="Feed, medicine, semen, equipment and spare parts — lot, expiry and the stock ledger (WF-07)."
      tabs={[
        { id: 'items', label: 'Items', render: () => <ResourceModule config={itemsConfig} /> },
        { id: 'lots', label: 'Lots', render: () => <ResourceModule config={lotsConfig} /> },
        { id: 'transactions', label: 'Transactions', render: () => <ResourceModule config={transactionsConfig} /> },
        { id: 'locations', label: 'Locations', render: () => <ResourceModule config={locationsConfig} /> },
        { id: 'feed-items', label: 'Feed Items', render: () => <ResourceModule config={feedItemsConfig} /> },
        { id: 'feed-consumption', label: 'Feed Consumption', render: () => <ResourceModule config={feedConsumptionConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Inventory', itemsConfig, locationsConfig, lotsConfig, transactionsConfig, feedItemsConfig, feedConsumptionConfig);
